import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useI18n } from "../../i18n/i18n";
import { fetchJson } from "../../lib/api";
import { getDatesInRange } from "../../lib/date-utils";
import { channelLabel, deviationDkkClass, formatAmount, formatSignedDkk } from "../../lib/format-utils";
import { buildDashboardMonthlyRows, summarizeNegativeDeviations } from "../../lib/dashboard-utils";
import type {
  BankAccount,
  BankTransaction,
  BookingEntry,
  BookingResponse,
  ChannelId,
  ConsumptionResponse,
  ReceivableJournalEntry,
  WorkerJobLog,
} from "../../lib/types";
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../table";

const requiredIntegrationChannels = ["eloverblik", "villavilla", "admiralstrand"] as const;
const redDeviationThresholdDkk = -300;
const winterNightLimit = 63;
const winterWarningThresholdNights = 7;

const getRelevantWinterSeasonStartYears = (currentDate: Date) => {
  const firstStartYear = currentDate.getUTCFullYear() - 1;
  return [firstStartYear, firstStartYear + 1] as const;
};

type CredentialsResponse = {
  channels: ChannelId[];
};

type ChartRow = {
  month: number;
  monthLabel: string;
  bookedDays: number;
  revenueDkk: number;
};

type ReceivablesSummaryRow = {
  channel: ChannelId;
  count: number;
  amount: number;
};

type BankWorkSummary = {
  unsettledCount: number;
  latestImportAt: string | null;
};

type WinterSeasonMetric = {
  seasonLabel: string;
  bookedNights: number;
  allowedNights: number;
  remainingNights: number;
  exceededNights: number;
  usagePercent: number;
};

const formatDateTime = (value: string, locale: "da" | "en") =>
  new Intl.DateTimeFormat(locale === "da" ? "da-DK" : "en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));

const formatChartNumber = (value: number, locale: "da" | "en") =>
  new Intl.NumberFormat(locale === "da" ? "da-DK" : "en-US", {
    maximumFractionDigits: 0,
  }).format(value);

export const DashboardView = ({
  houseId,
  logs,
  search,
  navigate,
}: {
  houseId: number | null;
  logs: WorkerJobLog[];
  search: string;
  navigate: (path: string, search?: string) => void;
}) => {
  const { t, locale } = useI18n();
  const [selectedYear, setSelectedYear] = useState(new Date().getUTCFullYear());
  const [bookingsData, setBookingsData] = useState<BookingResponse | null>(null);
  const [consumptionData, setConsumptionData] = useState<ConsumptionResponse | null>(null);
  const [credentials, setCredentials] = useState<CredentialsResponse | null>(null);
  const [receivables, setReceivables] = useState<ReceivableJournalEntry[]>([]);
  const [bankWorkSummary, setBankWorkSummary] = useState<BankWorkSummary | null>(null);
  const [winterBookingsByYear, setWinterBookingsByYear] = useState<Record<number, BookingEntry[]>>({});
  const [winterMetricsLoading, setWinterMetricsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!houseId) {
      setCredentials(null);
      setConsumptionData(null);
      setReceivables([]);
      setBankWorkSummary(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    Promise.all([
      fetchJson<CredentialsResponse>(`/api/houses/${houseId}/credentials`),
      fetchJson<ConsumptionResponse>(`/api/houses/${houseId}/consumption`),
      fetchJson<{ receivables: ReceivableJournalEntry[] }>(`/api/houses/${houseId}/receivables?settled=false`),
      (async () => {
        const accountsResponse = await fetchJson<{ accounts: BankAccount[] }>(
          `/api/houses/${houseId}/bank-accounts`,
        );
        const transactionResponses = await Promise.all(
          accountsResponse.accounts.map((account) =>
            fetchJson<{ transactions: BankTransaction[] }>(
              `/api/houses/${houseId}/bank-transactions?account=${account.id}`,
            ),
          ),
        );
        const allTransactions = transactionResponses.flatMap((response) => response.transactions);
        const unsettledCount = allTransactions.filter(
          (transaction) => transaction.linkStatus !== "linked",
        ).length;
        const latestImportAt =
          allTransactions.length === 0
            ? null
            : allTransactions.reduce(
                (latest, transaction) =>
                  latest === null || transaction.createdAt > latest ? transaction.createdAt : latest,
                null as string | null,
              );
        return { unsettledCount, latestImportAt };
      })(),
    ])
      .then(([credentialsResponse, consumptionResponse, receivablesResponse, bankSummary]) => {
        setCredentials(credentialsResponse);
        setConsumptionData(consumptionResponse);
        setReceivables(receivablesResponse.receivables);
        setBankWorkSummary(bankSummary);
      })
      .catch((loadError) => {
        setError(
          loadError instanceof Error
            ? loadError.message
            : t("dashboard.loadFailed", "Failed to load dashboard"),
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [houseId, t]);

  useEffect(() => {
    if (!houseId) {
      setBookingsData(null);
      return;
    }
    fetchJson<BookingResponse>(`/api/bookings?house=${houseId}&year=${selectedYear}`)
      .then((response) => {
        setBookingsData(response);
        if (response.year !== selectedYear) {
          setSelectedYear(response.year);
        }
      })
      .catch(() => {
        setBookingsData(null);
      });
  }, [houseId, selectedYear]);

  useEffect(() => {
    if (!houseId) {
      setWinterBookingsByYear({});
      setWinterMetricsLoading(false);
      return;
    }
    const [firstSeasonYear, secondSeasonYear] = getRelevantWinterSeasonStartYears(new Date());
    const yearsToLoad = Array.from(
      new Set([firstSeasonYear, firstSeasonYear + 1, secondSeasonYear + 1]),
    ).filter((year) => year !== selectedYear);
    setWinterMetricsLoading(true);
    Promise.all(
      yearsToLoad.map(async (year) => {
        const response = await fetchJson<BookingResponse>(`/api/bookings?house=${houseId}&year=${year}`);
        return [year, response.bookings] as const;
      }),
    )
      .then((responses) => {
        const nextByYear: Record<number, BookingEntry[]> = {};
        for (const [year, bookings] of responses) {
          nextByYear[year] = bookings;
        }
        setWinterBookingsByYear(nextByYear);
      })
      .catch(() => {
        setWinterBookingsByYear({});
      })
      .finally(() => {
        setWinterMetricsLoading(false);
      });
  }, [houseId, selectedYear]);

  const availableYears = useMemo(() => {
    if (!bookingsData) {
      return [] as number[];
    }
    return bookingsData.years.length > 0 ? bookingsData.years : [bookingsData.year];
  }, [bookingsData]);

  const chartRows = useMemo(() => {
    if (!bookingsData) {
      return [] as ChartRow[];
    }
    return buildDashboardMonthlyRows(bookingsData, locale);
  }, [bookingsData, locale]);

  const missingIntegrations = useMemo(
    () =>
      requiredIntegrationChannels.filter((channelId) => !(credentials?.channels ?? []).includes(channelId)),
    [credentials],
  );

  const latestLogByChannel = useMemo(() => {
    const byChannel = new Map<ChannelId, WorkerJobLog>();
    for (const log of logs) {
      if (log.houseId !== houseId) {
        continue;
      }
      const existing = byChannel.get(log.channel);
      if (!existing || log.startedAt > existing.startedAt) {
        byChannel.set(log.channel, log);
      }
    }
    return byChannel;
  }, [houseId, logs]);

  const failedIntegrations = useMemo(
    () =>
      requiredIntegrationChannels
        .map((channelId) => ({ channelId, log: latestLogByChannel.get(channelId) ?? null }))
        .filter((item) => item.log?.status === "error"),
    [latestLogByChannel],
  );

  const showIntegrationLoading = isLoading && credentials === null;
  const showIntegrationCard =
    !showIntegrationLoading && (missingIntegrations.length > 0 || failedIntegrations.length > 0);

  const { filteredRows: negativeDeviationRows, totals: negativeDeviationTotals } = useMemo(
    () => summarizeNegativeDeviations(consumptionData?.comparison.rows ?? [], redDeviationThresholdDkk),
    [consumptionData],
  );

  const receivablesByChannel = useMemo(() => {
    const summary = new Map<ChannelId, ReceivablesSummaryRow>();
    for (const entry of receivables) {
      if (!entry.channel) {
        continue;
      }
      const current = summary.get(entry.channel) ?? { channel: entry.channel, count: 0, amount: 0 };
      current.count += 1;
      current.amount += entry.amount;
      summary.set(entry.channel, current);
    }
    return Array.from(summary.values()).sort((left, right) =>
      channelLabel(left.channel).localeCompare(channelLabel(right.channel)),
    );
  }, [receivables]);

  const receivablesTotals = useMemo(
    () =>
      receivablesByChannel.reduce(
        (totals, row) => ({ count: totals.count + row.count, amount: totals.amount + row.amount }),
        { count: 0, amount: 0 },
      ),
    [receivablesByChannel],
  );
  const chartTickStyle = useMemo(() => ({ fontSize: 11 }), []);
  const chartTotals = useMemo(
    () =>
      chartRows.reduce(
        (totals, row) => ({
          bookedDays: totals.bookedDays + row.bookedDays,
          revenueDkk: totals.revenueDkk + row.revenueDkk,
        }),
        { bookedDays: 0, revenueDkk: 0 },
      ),
    [chartRows],
  );
  const activeChartYear = bookingsData?.year ?? selectedYear;
  const fullMonthYearFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale === "da" ? "da-DK" : "en-US", {
        month: "long",
        year: "numeric",
      }),
    [locale],
  );
  const winterSeasonMetrics = useMemo(() => {
    const bookingsByYear = { ...winterBookingsByYear };
    if (bookingsData) {
      bookingsByYear[bookingsData.year] = bookingsData.bookings;
    }
    const seasonStartYears = getRelevantWinterSeasonStartYears(new Date());
    return seasonStartYears.map((seasonStartYear): WinterSeasonMetric => {
      const seasonStartDate = `${seasonStartYear}-10-31`;
      const seasonEndDateExclusive = `${seasonStartYear + 1}-03-01`;
      const bookedDates = new Set<string>();
      for (const bookings of Object.values(bookingsByYear)) {
        for (const booking of bookings) {
          if (booking.status !== "booked") {
            continue;
          }
          const bookingDates = getDatesInRange(booking.startDate, booking.endDate);
          for (const date of bookingDates) {
            if (date >= seasonStartDate && date < seasonEndDateExclusive) {
              bookedDates.add(date);
            }
          }
        }
      }
      const bookedNights = bookedDates.size;
      const exceededNights = Math.max(0, bookedNights - winterNightLimit);
      const remainingNights = Math.max(0, winterNightLimit - bookedNights);
      const usagePercent = (bookedNights / winterNightLimit) * 100;
      return {
        seasonLabel: `${seasonStartYear}/${seasonStartYear + 1}`,
        bookedNights,
        allowedNights: winterNightLimit,
        remainingNights,
        exceededNights,
        usagePercent,
      };
    });
  }, [bookingsData, winterBookingsByYear]);

  if (!houseId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("dashboard.title", "Dashboard")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted">
            {t("dashboard.selectHouse", "Select a house to view the dashboard.")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-lg font-semibold text-ink">{t("dashboard.title", "Dashboard")}</p>
      </div>
      {isLoading ? <p className="text-sm text-muted">{t("dashboard.loading", "Loading dashboard...")}</p> : null}
      {error ? <p className="text-sm text-error">{error}</p> : null}

      {showIntegrationLoading ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("dashboard.integration.title", "Integration health")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted">
              {t("dashboard.integration.loading", "Loading integration health...")}
            </p>
          </CardContent>
        </Card>
      ) : showIntegrationCard ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("dashboard.integration.title", "Integration health")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {missingIntegrations.length > 0 ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-semibold">
                  {t("dashboard.integration.missing", "Missing setup for {count} channel(s).", {
                    count: missingIntegrations.length,
                  })}
                </p>
                <ul className="mt-2 list-disc space-y-1 pl-5">
                  {missingIntegrations.map((channelId) => (
                    <li key={channelId}>{channelLabel(channelId)}</li>
                  ))}
                </ul>
                <p className="mt-2">
                  {t(
                    "dashboard.integration.setupHint",
                    "Open Settings, connect each missing channel, and run an import to initialize data.",
                  )}
                </p>
              </div>
            ) : null}

            {failedIntegrations.length > 0 ? (
              <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                <p className="font-semibold">
                  {t(
                    "dashboard.integration.failed",
                    "Last import failed for {count} channel(s).",
                    { count: failedIntegrations.length },
                  )}
                </p>
                <ul className="mt-2 space-y-2">
                  {failedIntegrations.map(({ channelId, log }) =>
                    log ? (
                      <li key={channelId} className="rounded border border-red-200 bg-white p-2">
                        <p className="font-medium">{channelLabel(channelId)}</p>
                        <p>
                          {t("dashboard.integration.lastJob", "Job: {kind} at {time}", {
                            kind: log.kind,
                            time: formatDateTime(log.startedAt, locale),
                          })}
                        </p>
                        <p>{log.error || t("dashboard.integration.unknownError", "Unknown import error.")}</p>
                      </li>
                    ) : null,
                  )}
                </ul>
              </div>
            ) : null}

            <div>
              <Button type="button" variant="outline" onClick={() => navigate("/settings", search)}>
                {t("dashboard.integration.openSettings", "Open settings")}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <CardTitle>{t("dashboard.bookings.title", "Booked days and revenue by month")}</CardTitle>
              <p className="text-sm text-muted">
                {t("dashboard.bookings.subtitle", "Booked days (left axis) and revenue DKK (right axis).")}
              </p>
            </div>
            {availableYears.length > 0 ? (
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-ink" htmlFor="dashboard-year">
                  {t("common.year", "Year")}
                </label>
                <select
                  id="dashboard-year"
                  className="h-9 rounded-md border px-3 text-sm brand-input brand-focus"
                  value={String(selectedYear)}
                  onChange={(event) => setSelectedYear(Number.parseInt(event.target.value, 10))}
                >
                  {availableYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          {chartRows.length === 0 ? (
            <p className="text-sm text-muted">
              {t("dashboard.bookings.empty", "No booking data available for the selected year.")}
            </p>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-md border p-3">
                  <p className="text-xs uppercase tracking-wide text-muted">
                    {t("dashboard.bookings.bookedDays", "Booked days")}
                  </p>
                  <p className="text-2xl font-semibold text-ink">
                    {formatChartNumber(chartTotals.bookedDays, locale)}
                  </p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs uppercase tracking-wide text-muted">
                    {t("dashboard.bookings.revenue", "Revenue DKK")}
                  </p>
                  <p className="text-2xl font-semibold text-ink">
                    {formatAmount(chartTotals.revenueDkk, "DKK")}
                  </p>
                </div>
              </div>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartRows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="monthLabel" tick={chartTickStyle} />
                    <YAxis
                      yAxisId="left"
                      allowDecimals={false}
                      domain={[0, 31]}
                      tick={chartTickStyle}
                      tickFormatter={(value: number) => formatChartNumber(value, locale)}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={chartTickStyle}
                      tickFormatter={(value: number) => `${formatChartNumber(value, locale)} DKK`}
                    />
                    <Tooltip
                      labelStyle={{ fontSize: 11, lineHeight: 1.1 }}
                      itemStyle={{ fontSize: 11, lineHeight: 1.1 }}
                      labelFormatter={(_, payload) => {
                        const month = payload?.[0]?.payload?.month;
                        if (typeof month !== "number") {
                          return "";
                        }
                        return fullMonthYearFormatter.format(new Date(Date.UTC(activeChartYear, month, 1)));
                      }}
                      formatter={(value, name) => {
                        const numericValue = typeof value === "number" ? value : 0;
                        if (name === "revenueDkk") {
                          return [
                            formatAmount(numericValue, "DKK"),
                            t("dashboard.bookings.revenue", "Revenue DKK"),
                          ];
                        }
                        return [numericValue, t("dashboard.bookings.bookedDays", "Booked days")];
                      }}
                    />
                    <Legend
                      wrapperStyle={chartTickStyle}
                      formatter={(value) =>
                        value === "bookedDays"
                          ? t("dashboard.bookings.bookedDays", "Booked days")
                          : t("dashboard.bookings.revenue", "Revenue DKK")
                      }
                    />
                    <Bar yAxisId="left" dataKey="bookedDays" fill="#0ea5e9" />
                    <Bar yAxisId="right" dataKey="revenueDkk" fill="#22c55e" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("dashboard.winterRule.title", "Winter usage limit (Denmark)")}</CardTitle>
          <p className="text-sm text-muted">
            {t(
              "dashboard.winterRule.subtitle",
              "Booked nights in the winter period (31.10-28.02). Legal maximum: 63 nights per season.",
            )}
          </p>
        </CardHeader>
        <CardContent>
          {winterMetricsLoading ? (
            <p className="text-sm text-muted">
              {t("dashboard.winterRule.loading", "Loading winter season usage...")}
            </p>
          ) : (
            <div className="space-y-3">
              {winterSeasonMetrics.map((metric) => (
                <div key={metric.seasonLabel} className="rounded-md border p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-medium text-ink">
                      {t("dashboard.winterRule.season", "Season {season}", { season: metric.seasonLabel })}
                    </p>
                    <p className="text-sm text-muted">
                      {t("dashboard.winterRule.nightsUsed", "{booked} / {allowed} nights booked", {
                        booked: metric.bookedNights,
                        allowed: metric.allowedNights,
                      })}
                    </p>
                  </div>
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                    <div
                      className={
                        metric.exceededNights > 0
                          ? "h-full bg-red-600"
                          : metric.remainingNights <= winterWarningThresholdNights
                            ? "h-full bg-amber-500"
                            : "h-full bg-emerald-600"
                      }
                      style={{ width: `${Math.min(100, metric.usagePercent)}%` }}
                    />
                  </div>
                  <p
                    className={
                      metric.exceededNights > 0 ? "mt-2 text-xs text-red-700" : "mt-2 text-xs text-muted"
                    }
                  >
                    {metric.exceededNights > 0
                      ? t("dashboard.winterRule.exceeded", "Limit exceeded by {count} nights.", {
                          count: metric.exceededNights,
                        })
                      : t("dashboard.winterRule.remaining", "{count} nights remaining.", {
                          count: metric.remainingNights,
                        })}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("dashboard.deviation.title", "High consumption deviations")}</CardTitle>
          <p className="text-sm text-muted">
            {t(
              "dashboard.deviation.subtitle",
              "Consumption settlements where payment is more than 300 DKK lower than consumption based on ElOverblik.dk.",
            )}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {negativeDeviationRows.length === 0 ? (
            <p className="text-sm text-muted">
              {t("dashboard.deviation.empty", "No rows exceed the negative deviation threshold.")}
            </p>
          ) : (
            <Table className="text-[11px] sm:text-[11px]">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("consumption.booking", "Booking")}</TableHead>
                  <TableHead>{t("common.channel", "Channel")}</TableHead>
                  <TableHead>{t("consumption.period", "Period")}</TableHead>
                  <TableHead>{t("consumption.paidForDerived", "Paid for")} (kWh)</TableHead>
                  <TableHead>{t("consumption.eloverblikKwh", "ElOverblik (kWh)")}</TableHead>
                  <TableHead>{t("consumption.deviationDkk", "Deviation DKK")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {negativeDeviationRows.map((row) => (
                  <TableRow key={`${row.channel}:${row.bookingId}:${row.periodStart}:${row.periodEnd}`}>
                    <TableCell>{row.bookingId}</TableCell>
                    <TableCell>{channelLabel(row.channel)}</TableCell>
                    <TableCell>
                      {row.periodStart} → {row.periodEnd}
                    </TableCell>
                    <TableCell>{row.paidForKwh.toFixed(2)}</TableCell>
                    <TableCell>{row.eloverblikKwh.toFixed(2)}</TableCell>
                    <TableCell className={deviationDkkClass(row.deviationDkk)}>
                      {formatSignedDkk(row.deviationDkk)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="font-semibold">
                  <TableCell>{t("dashboard.total", "Total")}</TableCell>
                  <TableCell>—</TableCell>
                  <TableCell>—</TableCell>
                  <TableCell>{negativeDeviationTotals.paidForKwh.toFixed(2)}</TableCell>
                  <TableCell>{negativeDeviationTotals.eloverblikKwh.toFixed(2)}</TableCell>
                  <TableCell className={deviationDkkClass(negativeDeviationTotals.deviationDkk)}>
                    {formatSignedDkk(negativeDeviationTotals.deviationDkk)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
          <div>
            <Button type="button" variant="outline" onClick={() => navigate("/meter-readings", search)}>
              {t("dashboard.deviation.openOverview", "Open booking consumption overview")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("dashboard.receivables.title", "Receivables")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {receivablesByChannel.length === 0 ? (
            <p className="text-sm text-muted">
              {t("dashboard.receivables.empty", "No unsettled receivables found.")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("common.channel", "Channel")}</TableHead>
                  <TableHead>{t("dashboard.receivables.count", "Count")}</TableHead>
                  <TableHead>{t("dashboard.receivables.amount", "Amount")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {receivablesByChannel.map((row) => (
                  <TableRow key={row.channel}>
                    <TableCell>{channelLabel(row.channel)}</TableCell>
                    <TableCell>{row.count}</TableCell>
                    <TableCell>{formatAmount(row.amount, "DKK")}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="font-semibold">
                  <TableCell>{t("dashboard.total", "Total")}</TableCell>
                  <TableCell>{receivablesTotals.count}</TableCell>
                  <TableCell>{formatAmount(receivablesTotals.amount, "DKK")}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
          <div>
            <Button type="button" variant="outline" onClick={() => navigate("/receivables", search)}>
              {t("dashboard.receivables.openPage", "Open receivables")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("dashboard.bank.title", "Bank transaction follow-up")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(bankWorkSummary?.unsettledCount ?? 0) > 0 ? (
            <p className="text-sm">
              {t("dashboard.bank.unsettledCount", "Unsettled bank transactions: {count}", {
                count: bankWorkSummary?.unsettledCount ?? 0,
              })}
            </p>
          ) : null}
          <p className="text-sm">
            {bankWorkSummary?.latestImportAt
              ? t("dashboard.bank.latestImport", "Latest import: {date}", {
                  date: formatDateTime(bankWorkSummary.latestImportAt, locale),
                })
              : t("dashboard.bank.noImports", "No bank transaction imports yet.")}
          </p>
          <p className="text-sm text-muted">
            {(bankWorkSummary?.unsettledCount ?? 0) > 0
              ? t("dashboard.bank.todoHint", "There are transactions waiting to be settled.")
              : t("dashboard.bank.noTodo", "No unsettled transactions right now.")}
          </p>
          <div>
            <Button type="button" variant="outline" onClick={() => navigate("/bank-transactions", search)}>
              {t("dashboard.bank.openPage", "Open bank transactions")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
